/**
 * HistoryManager - Undo/Redo state management
 *
 * Manages history locally in the runtime (not on server).
 * Takes snapshots of graph definitions and can restore them.
 */

import type { Runtime } from './Runtime';
import type { GraphDefinition } from '../graph/GraphDefinition';

interface StateSnapshot {
  definition: GraphDefinition;
}

const MAX_UNDO_STACK_SIZE = 50;

export class HistoryManager {
  private runtime: Runtime;
  private undoStack: StateSnapshot[] = [];
  private redoStack: StateSnapshot[] = [];

  constructor(runtime: Runtime) {
    this.runtime = runtime;
  }

  /**
   * Create a snapshot of the current root graph state
   */
  private createSnapshot(): StateSnapshot | null {
    const rootDef = this.runtime.getGraphDefinition('root');
    if (!rootDef) return null;

    return {
      definition: JSON.parse(JSON.stringify(rootDef))
    };
  }

  /**
   * Restore a snapshot (updates definition and rebuilds instance)
   */
  private restoreSnapshot(snapshot: StateSnapshot): void {
    // Register the restored definition
    this.runtime.registerGraphDefinition(JSON.parse(JSON.stringify(snapshot.definition)));

    // Apply state will rebuild the instance
    this.runtime.setState({
      definitions: {
        root: snapshot.definition
      }
    });
  }

  /**
   * Push current state to undo stack (call before making changes)
   */
  push(): void {
    const snapshot = this.createSnapshot();
    if (!snapshot) return;

    this.undoStack.push(snapshot);
    if (this.undoStack.length > MAX_UNDO_STACK_SIZE) {
      this.undoStack.shift();
    }
    this.redoStack = [];
  }

  /**
   * Undo the last change
   */
  undo(): boolean {
    if (this.undoStack.length === 0) return false;

    const currentSnapshot = this.createSnapshot();
    if (currentSnapshot) {
      this.redoStack.push(currentSnapshot);
    }

    const previousSnapshot = this.undoStack.pop()!;
    this.restoreSnapshot(previousSnapshot);
    return true;
  }

  /**
   * Redo the last undone change
   */
  redo(): boolean {
    if (this.redoStack.length === 0) return false;

    const currentSnapshot = this.createSnapshot();
    if (currentSnapshot) {
      this.undoStack.push(currentSnapshot);
    }

    const redoSnapshot = this.redoStack.pop()!;
    this.restoreSnapshot(redoSnapshot);
    return true;
  }

  /**
   * Check if undo is available
   */
  canUndo(): boolean {
    return this.undoStack.length > 0;
  }

  /**
   * Check if redo is available
   */
  canRedo(): boolean {
    return this.redoStack.length > 0;
  }

  /**
   * Clear undo/redo history
   */
  clear(): void {
    this.undoStack = [];
    this.redoStack = [];
  }
}
